import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { saveAs } from "file-saver-es";
import { WorkCount, WorkHistory } from "../../../../shared/models/workHistory";
import {
  downloadFile,
  getParameter,
  saveParameters,
  updateTasksForEditedResponsibles,
} from "../api.service";
import { getApp } from "../app";
import { Parameter } from "../../../../shared/models/parameter";
import { Customer } from "../../../../shared/types/customers";
import { EncodingUtils } from "../../../../shared/utils/encoding.utils";
import { DataProvider } from "../../../../shared/constants/dataProvider";
import { writeConfigFile } from "../export/config-export";

@Component({
    selector: "app-operations",
    templateUrl: "./operations.component.html",
    styleUrls: ["./operations.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OperationsComponent implements OnInit, OnDestroy {
  @Input() showPaginator = true;
  public history: WorkHistory[] = [];
  public count: WorkCount | null = null;
  public workType:
    | "all"
    | "match"
    | "request-details"
    | "update-details"
    | "delete-docs"
    | "generate-tree"
    | "see-auditLogs"
    | "see-migrationHistory"
    | "update-impacts"
    | "generate-alerts"
    | "recalculate-global-stock"
    | "build-bom" = "match";
  public loading = false;
  public selectedMigrationHistory: any = null;

  parameterModel: Parameter = new Parameter();
  standardRangeValue = 0;

  app = getApp(async (app) => {
    this.app = app;
  });

  constructor() {}

  async ngOnInit(): Promise<void> {
    await this.getHistory();
    await this.getWorkCount();
    await this.app.users.getAllUserNames();

    this.parameterModel = await getParameter();
    this.standardRangeValue = this.parameterModel.standardRange;

    // TODO: find a better condition to set as default workType "generate-alerts" for team role
    if (
      this.app.customers.expectCurrent === Customer.MTU &&
      this.app.auth.isTeam
    ) {
      this.workType = "generate-alerts";
    }
  }

  async getHistory(): Promise<void> {
    this.history = await this.app.operations.getWorkHistory();
  }

  async getWorkCount(): Promise<void> {
    this.count = await this.app.operations.getWorkCount();

    if (
      !this.count &&
      this.app.customers.dataProvider === DataProvider.Z2DATA
    ) {
      await this.app.operations.createWorkCount();
      this.count = await this.app.operations.getWorkCount();
    }
  }

  async downloadLog(item: WorkHistory) {
    const fileLink = [
      this.app.customers.expectCurrent,
      "workHistory",
      EncodingUtils.encodeBase64(item._id),
      "log.txt",
    ].join("/");
    const data = await downloadFile(fileLink);
    const blob = new Blob([data], { type: data.type });

    saveAs(blob, "log.txt");
  }

  async runWork(): Promise<void> {
    this.loading = true;
    await this.app.operations.triggerWork(this.workType);
    this.loading = false;
    setTimeout(async () => {
      await this.getHistory();
    }, 5000);
  }

  async runUpdateTaskResponsibles() {
    this.app.spinner.showSpinner();
    await updateTasksForEditedResponsibles(this.app.customers.expectCurrent);
    this.app.spinner.hideSpinner();
  }

  async saveStandardRange() {
    const { parameterModel } = this;
    parameterModel.type = "parameter";
    await saveParameters(parameterModel);
    this.app.state.next({
      hasSuccess: true,
      successText: this.app.text.import.documentsSavedSuccesfully,
    });
    setTimeout(() => {
      this.app.state.next({ hasSuccess: false });
    }, 1000);
  }

  isDisabled(hasStandardRangeCheck: boolean) {
    if (this.workType === "generate-alerts") {
      const { parameterModel } = this;
      const standardRange = parameterModel["standardRange"];

      if (standardRange <= 0 || standardRange === undefined) {
        return true;
      }

      if (hasStandardRangeCheck && standardRange === this.standardRangeValue) {
        return true;
      }

      return false;
    }
  }

  get isAuditLogsView(): boolean {
    return this.workType === "see-auditLogs";
  }

  get isMigrationHistoryView(): boolean {
    return this.workType === "see-migrationHistory";
  }

  async getAuditLogs() {
    this.workType = "see-auditLogs";
    this.app.paginator.focus = 0;

    await this.app.operations.getAuditLogs();
  }

  async getMigrationHistory() {
    this.workType = "see-migrationHistory";
    this.app.paginator.focus = 0;
    this.selectedMigrationHistory = null;
    this.app.operations.migrationRuns = [];

    await this.app.operations.getMigrationHistory();

    if (this.app.operations.migrationHistory.length > 0) {
      this.selectedMigrationHistory = this.app.operations.migrationHistory[0];

      await this.getMigrationRuns(this.selectedMigrationHistory);
    }
  }

  async getMigrationRuns(item: any) {
    this.selectedMigrationHistory = item;

    await this.app.operations.getMigrationRuns(item.migration_id);
  }

  async exportAuditLogs() {
    await writeConfigFile(this.app, "auditLog");
  }

  ngOnDestroy(): void {}
}
